import merge from 'lodash.merge';
import { scrub_url_string } from './privacy';

// Deep value equality for the page_metadata dedupe. Scalars compare with ===; objects/arrays
// (e.g. refcodes) compare by stable JSON. Good enough here — the values on both sides come from
// the same builders, so key order is stable.
function metadata_values_match(first_value, second_value) {
	if (first_value === second_value) {
		return true;
	}
	if (
		first_value === null || second_value === null
		|| typeof first_value !== 'object' || typeof second_value !== 'object'
	) {
		return false;
	}
	try {
		return JSON.stringify(first_value) === JSON.stringify(second_value);
	} catch {
		return false;
	}
}

/**
 * Returns the subset of `source_page_metadata` that is NOT already represented identically in
 * `reference_metadata` (the higher-level web.metadata being sent). This is the "programmatic delta":
 * a key is dropped only when the SAME key holds the SAME value in the higher-level metadata; a key
 * absent there, or present with a DIFFERENT value, is kept (both are worth having). It is
 * self-maintaining by design — comparing against the actual assembled metadata means adding a new
 * metadata field later automatically dedupes it here, with no exclusion list to keep in sync.
 *
 * @param {Object|null|undefined} source_page_metadata
 * @param {Object} reference_metadata
 * @returns {Object} keys unique to (or differing from) the higher-level metadata
 */
function build_page_metadata_delta(source_page_metadata, reference_metadata) {
	if (!source_page_metadata || typeof source_page_metadata !== 'object') {
		return {};
	}
	const page_metadata_delta = {};
	for (const [key, value] of Object.entries(source_page_metadata)) {
		if (value === undefined) {
			continue;
		}
		if (
			Object.prototype.hasOwnProperty.call(reference_metadata, key)
			&& metadata_values_match(reference_metadata[key], value)
		) {
			continue;
		}
		page_metadata_delta[key] = value;
	}
	return page_metadata_delta;
}

function build_url_parameter_object(url_search_params) {
	const url_params = {};
	url_search_params.forEach((value, key) => {
		url_params[key] = value;
	});
	return url_params;
}

// Upper bound on distinct form ids collected per pageview. Well above any real page; guards a
// pathological DOM. Matches the analytics array cap in schema.js so nothing is silently dropped.
const maximum_page_form_ids = 50;
const maximum_page_form_id_length = 200;

/**
 * Collects the distinct `data-hub-form-id` values present in the current document — e.g. every
 * `<data-hub-signup-form data-hub-form-id="…">` (and any other element declaring a form id). Read
 * off the light-DOM host attribute, so it works even before the custom element upgrades/renders.
 * Attached to `$pageview` metadata so BigQuery can count pageviews that contained a given form
 * (the denominator for a conversion rate). Returns [] when the DOM is unavailable or has no forms.
 *
 * @returns {string[]} Distinct, trimmed form ids (order of first appearance), capped.
 */
export function collect_page_form_ids() {
	if (typeof document === 'undefined' || typeof document.querySelectorAll !== 'function') {
		return [];
	}
	try {
		const form_id_set = new Set();
		const form_id_elements = document.querySelectorAll('[data-hub-form-id]');
		for (const form_id_element of form_id_elements) {
			const form_id = form_id_element.getAttribute?.('data-hub-form-id');
			if (typeof form_id === 'string' && form_id.trim()) {
				form_id_set.add(form_id.trim().slice(0, maximum_page_form_id_length));
			}
			if (form_id_set.size >= maximum_page_form_ids) {
				break;
			}
		}
		return Array.from(form_id_set);
	} catch {
		return [];
	}
}

function get_meta_content(selector) {
	try {
		return document.querySelector(selector)?.getAttribute('content') || null;
	} catch {
		return null;
	}
}

function get_referring_domain(referrer) {
	if (!referrer) {
		return null;
	}
	try {
		return new URL(referrer).host || null;
	} catch {
		return null;
	}
}

function build_url_or_fallback(url_value) {
	try {
		return new URL(url_value || window.location.href, window.location.href);
	} catch {
		return new URL(window.location.href);
	}
}

/**
 * Resolves the raw page URL to scrub. Prefers a per-event snapshot (set at capture time by
 * build_base_analytics_event via build_page_metadata) so batch and per-event metadata reflect
 * the URL where the event fired, not the URL at flush time.
 */
function resolve_raw_page_url(data_hub_instance, event_metadata) {
	if (event_metadata && typeof event_metadata.page_url === 'string' && event_metadata.page_url) {
		return event_metadata.page_url;
	}
	if (typeof window !== 'undefined' && window.location?.href) {
		return window.location.href;
	}
	return data_hub_instance?.page_metadata?.page_url || null;
}

export function build_page_metadata(data_hub_instance = window?.data_hub, event_metadata = {}) {
	const raw_page_url = resolve_raw_page_url(data_hub_instance, event_metadata);
	const location_url = build_url_or_fallback(raw_page_url);
	const scrubbed_page_url = scrub_url_string(location_url.toString());
	const scrubbed_referrer = scrub_url_string(document.referrer || '');
	const scrubbed_url = build_url_or_fallback(scrubbed_page_url);
	const url_params = build_url_parameter_object(scrubbed_url.searchParams);
	// Refcodes and iterable ids are populated by set_page_metadata_defaults in
	// data_hub_metadata.js, which runs synchronously in the constructor before
	// init_analytics_bootstrap and well before any analytics flush. Falling back to
	// url_params here covers SPA route changes that introduce new params after init.
	const base_metadata = {
		page_url: scrubbed_page_url,
		canonical_url: document.querySelector('link[rel="canonical"]')?.href || null,
		origin: location_url.origin,
		protocol: location_url.protocol,
		host: location_url.host,
		pathname: location_url.pathname,
		search: scrubbed_url.search,
		hash: location_url.hash || '',
		referer: scrubbed_referrer,
		referring_domain: get_referring_domain(scrubbed_referrer),
		page_title: document.title || data_hub_instance?.page_metadata?.page_title || get_meta_content('meta[name="title"]') || null,
		page_description: get_meta_content('meta[name="description"]'),
		og_title: get_meta_content('meta[property="og:title"]'),
		og_description: get_meta_content('meta[property="og:description"]'),
		og_image: get_meta_content('meta[property="og:image"]'),
		content_language: document.documentElement?.lang || null,
		url_params,
		utm_source: url_params.utm_source || null,
		utm_medium: url_params.utm_medium || null,
		utm_campaign: url_params.utm_campaign || null,
		utm_term: url_params.utm_term || null,
		utm_content: url_params.utm_content || null,
		utm_id: url_params.utm_id || null,
		gclid: url_params.gclid || null,
		fbclid: url_params.fbclid || null,
		msclkid: url_params.msclkid || null,
		refcode: url_params.refcode || data_hub_instance?.page_metadata?.refcodes?.refcode || null,
		refcode2: url_params.refcode2 || data_hub_instance?.page_metadata?.refcodes?.refcode2 || null,
		refcodes: data_hub_instance?.page_metadata?.refcodes || {},
		iterableCampaignId: data_hub_instance?.iterableCampaignId || null,
		iterableTemplateId: data_hub_instance?.iterableTemplateId || null,
		attribution_touch: null,
		is_spa_navigation: false,
		navigation_type: typeof performance !== 'undefined' ? performance.getEntriesByType?.('navigation')?.[0]?.type || null : null
	};

	const merged_metadata = merge(
		{},
		data_hub_instance?.page_metadata || {},
		base_metadata,
		event_metadata || {}
	);

	// Opt-in (the $pageview impression sets metadata.include_page_metadata): attach the DataHub
	// instance page_metadata as a JSON sub-blob, minus any key already represented identically in
	// the higher-level metadata. The reference is the metadata WITHOUT page_metadata's own
	// contribution (document-derived base + caller event metadata), so a host's custom page context
	// survives while duplicated known fields (page_url, refcodes, …) drop out — no exclusion list.
	// Whole block is best-effort: page_metadata is host-controlled data, so a bad shape must never
	// break the $pageview — on any failure we simply omit page_metadata rather than throw.
	if (event_metadata && event_metadata.include_page_metadata) {
		delete merged_metadata.include_page_metadata;
		try {
			const reference_metadata = merge({}, base_metadata, event_metadata);
			delete reference_metadata.include_page_metadata;
			const page_metadata_delta = build_page_metadata_delta(
				data_hub_instance?.page_metadata,
				reference_metadata
			);
			if (Object.keys(page_metadata_delta).length > 0) {
				merged_metadata.page_metadata = page_metadata_delta;
			}
		} catch {
			// Omit page_metadata; the rest of the pageview metadata is unaffected.
		}
	}

	return merged_metadata;
}

/**
 * Keeps data_hub_instance.page_metadata aligned with the current document after an SPA route
 * change. Uses the same query-stripped page_url convention as set_page_metadata_defaults so
 * form submits stay privacy-safe while analytics events carry the full scrubbed URL.
 *
 * @param {Object} data_hub_instance
 * @returns {void}
 */
export function sync_instance_page_metadata_from_current_location(data_hub_instance) {
	if (!data_hub_instance?.page_metadata || typeof window === 'undefined') {
		return;
	}
	const fresh_metadata = build_page_metadata(data_hub_instance, {});
	if (typeof fresh_metadata.page_url === 'string' && fresh_metadata.page_url) {
		data_hub_instance.page_metadata.page_url = fresh_metadata.page_url.split('?')[0];
	}
	if (typeof fresh_metadata.page_title === 'string' && fresh_metadata.page_title) {
		data_hub_instance.page_metadata.page_title = fresh_metadata.page_title;
	}
}
